import React, { useState, useMemo, useEffect } from 'react';
import './_applicationDetails.scss'
import RequestStatusBadge from '../../../../common/base/RequestStatusBadge/RequestStatusBadge';
import RequestDetails from '../RequestDetails/RequestDetails';

const STATUS_ORDER = { pending: 0, invalid: 1, approved: 2, denied: 3 };

const approveIco = {"label":"approve", "variant":"check-outline","tooltip_label":'Approve Request'}
const denyIco = {"label":"deny", "tooltip_label":'Deny Request', "variant":"exit-outline"}
const removeIco = {"label":"remove", "tooltip_label":'Remove Request', "variant":"delete"}
const editTierIco = {"label":"editTier", "tooltip_label":'Edit SLA Tier', "variant":"draw"}
const statusAction = {
   pending:[approveIco, denyIco, editTierIco],
   approved: [denyIco, removeIco, editTierIco],
   denied: [approveIco, removeIco],
   invalid: [removeIco]
}
const tierChangeAction = [approveIco, denyIco];

function ApplicationDetails({ requests, onAction, onSelectionChange }) {
   const [selectedRows, setSelectedRows] = useState(new Set());

   useEffect(() => {
     if (onSelectionChange) {
       onSelectionChange(Array.from(selectedRows));
     }
   }, [selectedRows]);

   const pendingRequests = useMemo(() =>
    requests.filter(r => r.status.toLowerCase() === 'pending' && !r.isTierChange),
   [requests]);

   const handleSelectAll = (checked) => {
     if (checked) {
       setSelectedRows(new Set(pendingRequests.map(r => r.id)));
     } else {
       setSelectedRows(new Set());
     }
   };

   const handleSelectRow = (id) => {
     setSelectedRows(prev => {
       const next = new Set(prev);
       if (next.has(id)) {
         next.delete(id);
       } else {
         next.add(id);
       }
       return next;
     });
   };

   const isAllSelected = pendingRequests.length > 0 && selectedRows.size === pendingRequests.length;

   const sortedRequests = useMemo(() => {
    const tierChangeMap = new Map();
    const regularRows = [];
    requests.forEach(r => {
      if (r.isTierChange) {
        tierChangeMap.set(r.id, r);
      } else {
        regularRows.push(r);
      }
    });
    regularRows.sort((a, b) => {
      const hasPendingA = tierChangeMap.has(a.id) ? 1 : 0;
      const hasPendingB = tierChangeMap.has(b.id) ? 1 : 0;
      if (hasPendingA !== hasPendingB) return hasPendingB - hasPendingA;
      const orderA = STATUS_ORDER[a.status.toLowerCase()] ?? 99;
      const orderB = STATUS_ORDER[b.status.toLowerCase()] ?? 99;
      return orderA - orderB;
    });
    return { rows: regularRows, tierChangeMap };
  }, [requests]);

  const renderActionIcons = (actions, reqId, gateway, isTierChange) => (
    <div className="action">
      <div className="d-flex align-items-center">
        {actions?.map(action => (
          <hbr-tooltip key={action.label} size="large" content={action.tooltip_label} placement="top" class="atm-tooltip--large">
            <i class={`atm-icon ${action.variant}`} onClick={() => onAction && onAction(action.label, reqId, gateway, isTierChange)} />
          </hbr-tooltip>
        ))}
      </div>
    </div>
  );

  const getActionBtns = (status, reqId, gateway) => {
    return renderActionIcons(statusAction[status.toLowerCase()], reqId, gateway, false);
  };

   return (
    <table className="table application-details-table mb-0">
      <thead>
        <tr>
          <th className="inner-checkbox-col">
            {pendingRequests.length > 0 && (
              <input
                type="checkbox"
                className="inner-checkbox"
                checked={isAllSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            )}
          </th>
          <th>Application Name</th>
          <th>API Instance</th>
          <th>API Version</th>
          <th>Requester</th>
          <th>Requester email</th>
          <th>Status</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {sortedRequests.rows.map(request => {
          const tierChange = sortedRequests.tierChangeMap.get(request.id);
          const hasPendingChange = !!tierChange;

          return (
            <tr key={request.id} className={hasPendingChange ? 'has-pending-tier-change' : ''}>
              <td className="inner-checkbox-col">
                {request.status.toLowerCase() === 'pending' && (
                  <input
                    type="checkbox"
                    className="inner-checkbox"
                    checked={selectedRows.has(request.id)}
                    onChange={() => handleSelectRow(request.id)}
                  />
                )}
              </td>
              <td>
                <span className="d-flex align-items-center">
                  {request.applicationName}
                  <span className="info-icon-wrapper ms-2">
                    <i className="inner-info-icon"></i>
                    <RequestDetails request={request} />
                  </span>
                </span>
              </td>
              <td>{request.apiInstance || '-'}</td>
              <td>
                {request.apiDetails?.version
                  ? <span className="api-version-badge">{request.apiDetails.version}</span>
                  : '-'}
              </td>
              <td>{request.requesterName}</td>
              <td>{request.requesterEmail}</td>
              <td>
                <div className="status-cell">
                  <div className="status-main">
                    <RequestStatusBadge status={request.status} />
                  </div>
                  {hasPendingChange && (
                    <div className="status-pending">
                      <span className="pending-badge-wrapper">
                        <RequestStatusBadge status="Pending" />
                        <RequestDetails request={tierChange} />
                      </span>
                    </div>
                  )}
                </div>
              </td>
              <td>
                <div className="action-cell">
                  <div className="action-main">
                    {getActionBtns(request.status, request.id, request.gateway)}
                  </div>
                  {hasPendingChange && (
                    <div className="action-pending">
                      {renderActionIcons(tierChangeAction, request.id, request.gateway, true)}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
   )
}


export default ApplicationDetails;
